import React, { FC, useEffect, useState } from "react"
import {
  DateFormat,
  PuxItem,
  PuxModal,
  PuxSheet,
  PuxSkeletonText,
  useTranslation,
} from "@phonero/pux-react"
import {
  ActiveSimCardDetails,
  ActiveSimCardsDeviceInfoDocument,
  ActiveSimCardsDeviceInfoFragment,
  StepUpAuthentication,
  AuthenticationStepUpCache,
  AvailableAndActiveSimCardsDocument,
  dataCy,
  DeactivateSimCardModal,
  InfoBlock,
  InlineError,
  pollInterval,
  SimCardFragment,
  SimCardOrderFragment,
  SimCardProductFragment,
  SimCardsMobileSubscriptionFragmentDoc,
  Swiper,
  UpdateSimCardDocument,
  useAppMutation,
  useAppQuery,
  useAuthenticationStepUpCache,
  useBreakpointDesktop,
  useTokenData, UsageTypePersonalOnlyInfoBox,
  SimCardWithOrderButton,
  SimPaidByStatusMessage,
} from "@phonero/common-ux"
import {
  ApiProductState,
  OfferingContractCategory,
  SimCardType2,
} from "@phonero/common-graphql/types"
import ShinySimCard from "../ShinySimCard"
import { sheetStyles } from "../../../../appConstants"
import css from "./SimCardSlides.module.scss"
import classNames from "classnames"
import { SimCardWizard } from "../SimCardWizard/SimCardWizard"
import { useFeatureIsOn, useFeatureValue } from "@growthbook/growthbook-react"
import { QrCodeDetails } from "./QrCodeDetails"
import { SimReadyForActivationText } from "./SimReadyForActivationText"
import { ReadyForActivationDetails } from "./ReadyForActivationDetails"
import { SimCardList } from "./SimCardList"
import { phoneroConfig } from "../../../../appConstants"

export type GenericSimCardFragment =
  | SimCardFragment
  | SimCardProductFragment
  | SimCardOrderFragment

const apiStateOrder: Record<ApiProductState, number> = {
  [ApiProductState.Active]: 900,
  [ApiProductState.Available]: 800,
  [ApiProductState.Deactive]: 0,
  [ApiProductState.Error]: -100,
  [ApiProductState.Startactivation]: 600,
  [ApiProductState.Activating]: 700,
  [ApiProductState.Deactivating]: 600,
  [ApiProductState.Ordered]: 500,
  [ApiProductState.Registered]: 600,
}
// Where is SimcardType1?
const simCardOrder: Record<SimCardType2, number> = {
  [SimCardType2.Subscription]: 90,
  [SimCardType2.Twin]: 80,
  [SimCardType2.Datacard]: 70,
  [SimCardType2.Smartwatch]: 85,
}

export const SimCardSlides: FC<{
  products?: Array<SimCardFragment>
  editDenied?: boolean
}> = ({ products, editDenied }) => {
  const { subscriptionId } = useTokenData()
  const [selectedSimCard, setSelectedSimCard] = useState<SimCardFragment>()
  type GenericSimCardFragment =
    | SimCardFragment
    | SimCardProductFragment
    | SimCardOrderFragment
  const [orderSim, setOrderSim] = useState<GenericSimCardFragment>()

  const showEditSimWizard =
    useFeatureValue("dpw-edit-sim-wizard", true) &&
    selectedSimCard?.type !== SimCardType2.Smartwatch &&
    !editDenied
  const showTerminateSimOnSmartwatch = useFeatureIsOn(
    "selfservice-allow-deactivate-smartwatchsim"
  )
  const showVippsRecurringOrderSimCard = useFeatureIsOn(
      "DP-order-sim-vipps-recurring"
  )
  const [simCardToDeactivate, setSimCardToDeactivate] =
    useState<SimCardFragment>()

  const { i18n } = useTranslation()
  const { loading, data, startPolling, error, stopPolling } = useAppQuery(
    AvailableAndActiveSimCardsDocument,
    {
      variables: { id: subscriptionId! },
    }
  )

  const {
    loading: fragmentLoading,
    data: fragmentData,
    error: fragmentError,
  } = useAppQuery(ActiveSimCardsDeviceInfoDocument, {
    variables: { subscriptionId: subscriptionId! },
    fetchPolicy: "no-cache",
  })

  const {
    isCacheActive,
    cachedItem,
    clear: clearAuthenticationCache,
  } = useAuthenticationStepUpCache()

  // open replace sim wizard if cache is active
  useEffect(() => {
    if (
      !!isCacheActive &&
      cachedItem?.context === "ReplaceSim" &&
      !!cachedItem.replaceSim
    ) {
      setOrderSim(cachedItem.replaceSim.OrderSim)
      clearAuthenticationCache()
    }
  }, [isCacheActive, cachedItem, setOrderSim, clearAuthenticationCache])

  const replaceSimCache = (): AuthenticationStepUpCache => {
    const replaceSimAuthCache: AuthenticationStepUpCache = {
      context: "ReplaceSim",
      replaceSim: { OrderSim: orderSim },
    }
    return replaceSimAuthCache
  }

  const swiperBreakpoints = {
    "992": {
      peek: 180,
    },
    "820": {
      peek: 150,
    },
    "670": {
      peek: 110,
    },
    "590": {
      peek: 80,
    },
    "528": {
      peek: 60,
    },
    "485": {
      peek: 40,
    },
    "450": {
      peek: 25,
    },
    "415": {
      peek: 20,
    },
    "405": {
      peek: 10,
    },
    "385": {
      peek: 5,
    },
  }
  const enableSimWithoutPin =
    data?.mobileSubscription?.contractCategory === OfferingContractCategory.M2M
  const hasTansitionals = data?.mobileSubscription?.simCards?.some((p) => {
    if (!("state" in p)) {
      return false
    }
    switch (p.state) {
      case ApiProductState.Activating:
      case ApiProductState.Deactivating:
      case ApiProductState.Ordered:
        return true
    }
    return false
  })
  useEffect(() => {
    if (!hasTansitionals) {
      stopPolling()
      return
    }
    /* Do we need this polling?? It can be in state Ordered for a very long time. */
    startPolling(pollInterval(60000))
  }, [hasTansitionals, stopPolling, startPolling])

  const sortedProducts =
    products ||
    data?.mobileSubscription?.simCards
      ?.map((s) => s as SimCardFragment)
      .slice()
      .sort((a, b) => {
        const A =
          (apiStateOrder[a.state] || 0) +
          (("type" in a && simCardOrder[a.type]) || 0)
        const B =
          (apiStateOrder[b.state] || 0) +
          (("type" in b && simCardOrder[b.type]) || 0)
        const c = B - A
        if (c !== 0) {
          return c
        }
        if (a.name > b.name) {
          return 1
        }
        if (a.name < b.name) {
          return -1
        }
        return 0
      })

  const simCardsDeviceInfo = fragmentData?.mobileSubscription
    ?.products as Array<ActiveSimCardsDeviceInfoFragment>

  const selectedSimDeviceInfo =
    !!selectedSimCard &&
    simCardsDeviceInfo?.find((s) => s.id === selectedSimCard.id)

  const firstAvailableCard = (products ?? []).find(
    (product) => product.state === ApiProductState.Available
  )
    
  const availableToOrder = (products ?? []).filter(
    (product) => product.state === ApiProductState.Available
  )

  useEffect(() => {
    if (
      sortedProducts?.length &&
      sortedProducts.length >= 1 &&
      !selectedSimCard
    )
      setSelectedSimCard(sortedProducts[0] as SimCardFragment)
  }, [selectedSimCard, sortedProducts])

  const updateSimOptions = {
    variables: {
      input: {
        contractProductId: 0,
        newName: "",
      },
    },
    update: (cache, mutationResult) => {
      const mutatedSimCards = mutationResult?.data?.updateSimCard?.simCards
      if (mutatedSimCards)
        cache.writeFragment({
          id: `${data?.mobileSubscription?.__typename}:${data?.mobileSubscription?.id}`,
          fragment: SimCardsMobileSubscriptionFragmentDoc,
          fragmentName: "simCardsMobileSubscription",
          data: { ...data!.mobileSubscription, simCards: mutatedSimCards },
        })
      updateSimOptions.variables.input.contractProductId = 0
      updateSimOptions.variables.input.newName = ""
    },
  }
  const [updateSimCardMutation, { error: updateSimCardError }] = useAppMutation(
    UpdateSimCardDocument,
    {
      ...updateSimOptions,
    }
  )

  const updateSimCard = (
    contractProductId: number,
    newName: string
  ): Promise<any> => {
    updateSimOptions.variables.input.contractProductId = contractProductId
    updateSimOptions.variables.input.newName = newName
    return updateSimCardMutation()
  }

  const { __typename, ...savedAddress } =
    data?.mobileSubscription?.simCardAddress! ?? ""

  const isDesktop = useBreakpointDesktop()
  if (isDesktop) {
    return (
      <PuxSheet
        {...dataCy("sim-cards")}
        style={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          ...sheetStyles,
        }}
        color={"dark"}
      >
        <InlineError code="listActiveSIMs" error={error} />
        <InlineError code="updateSimCardMutation" error={updateSimCardError} />
        {!loading && !sortedProducts?.length && (
          <p style={{ textAlign: "center" }}>Ingen simkort registrert</p>
        )}
        {data?.mobileSubscription?.usageType === "IMPERSONAL" && (
          <div style={{justifySelf: "center" }}>
            <UsageTypePersonalOnlyInfoBox />
          </div>
        )}
        {!!sortedProducts?.length && (
          <SimCardList
            data={data}
            simCardDetailsloading={fragmentLoading}
            phoneNumber={data?.mobileSubscription?.msisdn}
            products={sortedProducts as any}
            count={3}
            animated={loading}
            updateSimCard={updateSimCard}
            firstAvailableCard={firstAvailableCard}
            availableToOrder={availableToOrder.length ?? 0}
            subscriptionExpectedActivationDate={
              data?.mobileSubscription?.expectedActivation
                ? new Date(data?.mobileSubscription?.expectedActivation)
                : undefined
            }
            subscriptionDeactivationDate={
              data?.mobileSubscription?.deactivationDate
                ? new Date(data?.mobileSubscription?.deactivationDate)
                : undefined
            }
            simCardsDeviceInfo={simCardsDeviceInfo}
          />
        )}
      </PuxSheet>
    )
  }
  const showPrice = phoneroConfig.collectorApplicationName !== "dpw"

  return (
    <PuxSheet
      noPadding
      color="dark"
      style={{ padding: "2rem 0", textAlign: "center", ...sheetStyles }}
      {...dataCy("sim-cards")}
    >
      {loading ? (
        <PuxSkeletonText
          animated={loading}
          style={{ height: 200, width: 350, margin: "auto" }}
        />
      ) : !sortedProducts?.length ? (
        <p style={{ textAlign: "center" }}>Ingen simkort registrert</p>
      ) : (
        <>
          {data?.mobileSubscription?.usageType === "IMPERSONAL" && (
            <div style={{ justifySelf: "center", marginInline: "40px" }}>
              <UsageTypePersonalOnlyInfoBox />
            </div>
          )}
          <Swiper
            dots={sortedProducts.length > 1}
            perView={1}
            gap={10}
            setBreakpoints={swiperBreakpoints}
            onSlideChange={(e) => {
              setSelectedSimCard({
                ...(sortedProducts![e] as SimCardFragment),
              })
            }}
          >
            {sortedProducts
              ?.filter(({ state }) => state !== ApiProductState.Available)
              .map((card: SimCardFragment, index) => {
                // const isReadyForActivation =
                //   card.state === "ACTIVE" && !card.activationDate
                // const state = isReadyForActivation
                //   ? "READY_FOR_ACTIVATION"
                //   : card.state
                return (
                  <>
                    <ShinySimCard
                      key={card.id ?? index}
                      noMargin
                      phoneNumber={data?.mobileSubscription?.msisdn}
                      updateSimCard={updateSimCard}
                      {...card}
                      subscriptionExpectedActivationDate={
                        data?.mobileSubscription?.expectedActivation
                          ? new Date(
                              data?.mobileSubscription?.expectedActivation
                            )
                          : undefined
                      }
                      subscriptionDeactivationDate={
                        data?.mobileSubscription?.deactivationDate
                          ? new Date(data?.mobileSubscription?.deactivationDate)
                          : undefined
                      }
                    />
                  </>
                )
              })}
          </Swiper>
          <PuxModal
            isOpen={!!orderSim}
            onDidDismiss={() => {
              setOrderSim(undefined)
              clearAuthenticationCache()
            }}
          >
            <StepUpAuthentication
              title="Erstatt SIM-kort"
              stepUpCache={replaceSimCache}
            >
              <SimCardWizard
                subscriptionId={subscriptionId ?? ""}
                enableSimWithoutPin={enableSimWithoutPin}
                card={orderSim as any}
                cardType={orderSim?.type!}
                address={savedAddress}
                onClose={() => setOrderSim(undefined)}
                userName={data?.mobileSubscription?.userName ?? undefined}
                msisdn={data?.mobileSubscription?.msisdn ?? undefined}
              />
            </StepUpAuthentication>
          </PuxModal>
          <>
            {selectedSimCard &&
              selectedSimCard.type !== SimCardType2.Subscription &&
              !!selectedSimCard.deactivationDate && (
                <InfoBlock
                  className={classNames(
                    css["info-block"],
                    css["info-block-mobile"]
                  )}
                >
                  Avsluttes{" "}
                  {i18n.format(
                    selectedSimCard!.deactivationDate,
                    DateFormat.DateTimeLong
                  )}
                </InfoBlock>
              )}
            {selectedSimCard &&
              selectedSimCard.state === ApiProductState.Active &&
              !selectedSimCard.embeddedSimIsReadyForActivation && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "left",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {fragmentLoading ? (
                    <PuxSkeletonText
                      animated={fragmentLoading}
                      style={{ width: 200 }}
                    />
                  ) : (
                    !fragmentLoading &&
                    selectedSimDeviceInfo && (
                      <>
                        {showVippsRecurringOrderSimCard &&
                          selectedSimCard.type !==
                            SimCardType2.Subscription && (
                            <SimPaidByStatusMessage paidBy="Betales privat med " />
                          )}
                        <ActiveSimCardDetails
                          deviceName={
                            selectedSimDeviceInfo.deviceInfo?.deviceName
                          }
                          lastUsed={selectedSimDeviceInfo.deviceInfo?.lastUsed}
                          usage={selectedSimDeviceInfo.usage ?? 1000}
                          activationDate={selectedSimCard.activationDate}
                          mode="light"
                          style={{ width: 275 }}
                          price={selectedSimCard.price}
                          showPrice={showPrice}
                        />
                      </>
                    )
                  )}
                  {showEditSimWizard && (
                    <>
                      <div className={css["sim-buttons-mobile"]}>
                        <PuxItem
                          type="button"
                          onClick={() =>
                            setOrderSim(
                              selectedSimCard as GenericSimCardFragment
                            )
                          }
                        >
                          Erstatt
                        </PuxItem>
                      </div>
                    </>
                  )}
                  {selectedSimCard.type === SimCardType2.Smartwatch &&
                    selectedSimCard.state === ApiProductState.Active &&
                    !selectedSimCard.deactivationDate &&
                    showTerminateSimOnSmartwatch && (
                      <PuxItem
                        type="button"
                        button={true}
                        onClick={() => setSimCardToDeactivate(selectedSimCard)}
                      >
                        Avslutt
                      </PuxItem>
                    )}
                  {!!simCardToDeactivate && (
                    <DeactivateSimCardModal
                      card={simCardToDeactivate}
                      isOpen={simCardToDeactivate !== undefined}
                      onDidDismiss={() => {
                        setSimCardToDeactivate(undefined)
                      }}
                      disableDateSelection={true}
                      showPrice={showPrice}
                    />
                  )}
                </div>
              )}
            {selectedSimCard &&
              selectedSimCard.state === ApiProductState.Startactivation &&
              !selectedSimCard.activationDate && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    textAlign: "left",
                  }}
                >
                  <ReadyForActivationDetails card={selectedSimCard} />
                </div>
              )}
            {selectedSimCard &&
              selectedSimCard.embeddedSimIsReadyForActivation && (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      textAlign: "left",
                      marginBottom: "20px",
                    }}
                  >
                    <SimReadyForActivationText card={selectedSimCard} />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      textAlign: "left",
                    }}
                  >
                    <QrCodeDetails card={selectedSimCard} />
                    {/* TODO: Add replace simcardbutton to standalone component */}
                    {showEditSimWizard && (
                      <>
                        <div
                          className={css["sim-buttons-mobile"]}
                          style={{ paddingLeft: "10px" }}
                        >
                          <PuxItem
                            type="button"
                            onClick={() =>
                              setOrderSim(
                                selectedSimCard as GenericSimCardFragment
                              )
                            }
                          >
                            Erstatt
                          </PuxItem>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
          </>
        </>
      )}
    </PuxSheet>
  )
}
